/**
 * Save Data - defines the structure for game saves
 */

import type { FactionId, BuildingType, UnitType } from '@/types';
import type { DifficultyLevel } from '@/game/ai/DifficultySettings';

export interface SaveData {
  version: number;
  timestamp: number;
  name: string;

  // Game config
  config: {
    seed: number;
    difficulty: DifficultyLevel;
    mapSize: number;
  };

  // Game state
  gameTime: number;
  gameSpeed: number;
  phase: string;

  // Player
  playerFaction: FactionId;
  playerGold: number;

  // Entities
  entities: SavedEntity[];

  // Faction states
  factionStates: SavedFactionState[];

  // Diplomacy
  diplomacy: SavedDiplomacy;

  // Tech
  techStates: SavedTechState[];

  // Vision
  visionStates: SavedVisionState[];
}

export interface SavedEntity {
  id: number;
  type: 'unit' | 'building';
  factionId: FactionId;
  position: { x: number; y: number; z: number };
  health: { current: number; max: number };

  // Unit specific
  unitType?: UnitType;
  targetId?: number | null;

  // Building specific
  buildingType?: BuildingType;
  spawnerData?: {
    unitType: UnitType;
    spawnInterval: number;
    currentCooldown: number;
    rallyX: number;
    rallyZ: number;
  };
}

export interface SavedFactionState {
  id: FactionId;
  gold: number;
  territoryCount: number;
  unitCount: number;
  buildingCount: number;
  isDefeated: boolean;
  isPlayer: boolean;
}

export interface SavedDiplomacy {
  relations: { key: string; value: number }[];
  treaties: SavedTreaty[];
}

export interface SavedTreaty {
  id: number;
  type: string;
  parties: [FactionId, FactionId];
  startTime: number;
  duration: number;
}

export interface SavedTechState {
  factionId: FactionId;
  researched: string[];
  lockedTechs: string[]; // Techs locked due to mutual exclusion
  currentResearch: {
    techId: string;
    progress: number;
  } | null;
}

export interface SavedVisionState {
  factionId: FactionId;
  gridData: number[]; // Compressed vision grid
}

export const SAVE_VERSION = 1;
export const MAX_SAVE_SLOTS = 3;

export function createEmptySaveData(): SaveData {
  return {
    version: SAVE_VERSION,
    timestamp: Date.now(),
    name: 'New Save',
    config: {
      seed: 0,
      difficulty: 'normal',
      mapSize: 256,
    },
    gameTime: 0,
    gameSpeed: 1,
    phase: 'menu',
    playerFaction: 'lin',
    playerGold: 500,
    entities: [],
    factionStates: [],
    diplomacy: { relations: [], treaties: [] },
    techStates: [],
    visionStates: [],
  };
}
